import type { OrganizationSettings, UpdateOrganizationInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { useToast } from '../../../shared/hooks/use-toast';
import { getCompanySettings, removeCompanyLogo, updateCompanySettings, uploadCompanyLogo, type LogoVariant } from '../company-settings.api';

export type CompanySettingsState = { status: 'loading' } | { status: 'loaded'; settings: OrganizationSettings } | { status: 'forbidden' } | { status: 'error' };

const LOGO_LABEL: Record<LogoVariant, string> = { light: 'Logo', dark: 'Dark-surface logo' };

/** Nav-hiding is UX only — a user who reaches this route without `configuration.manage` still gets a real 403 from the server. */
export function useCompanySettings() {
  const [state, setState] = useState<CompanySettingsState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingLogo, setSavingLogo] = useState<LogoVariant | null>(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    getCompanySettings()
      .then((settings) => {
        if (!cancelled) setState({ status: 'loaded', settings });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Returns whether it succeeded, so the form knows it saved — same shape as useUsers().create. */
  async function save(input: UpdateOrganizationInput): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      const settings = await updateCompanySettings(input);
      setState({ status: 'loaded', settings });
      toast.success('Company settings updated');
      return true;
    } catch {
      setSaveError('Could not save company settings — check the fields and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  /** Row-action style, like the users table: no form of its own, so failures toast rather than showing inline. */
  async function saveLogo(variant: LogoVariant, action: () => Promise<OrganizationSettings>, pastTense: string, verb: string) {
    setSavingLogo(variant);
    try {
      const settings = await action();
      setState({ status: 'loaded', settings });
      toast.success(`${LOGO_LABEL[variant]} ${pastTense}`);
    } catch {
      toast.error(`Could not ${verb} the ${LOGO_LABEL[variant].toLowerCase()} — try again.`);
    } finally {
      setSavingLogo(null);
    }
  }

  const uploadLogo = (variant: LogoVariant, file: File) => saveLogo(variant, () => uploadCompanyLogo(variant, file), 'updated', 'update');
  const removeLogo = (variant: LogoVariant) => saveLogo(variant, () => removeCompanyLogo(variant), 'removed', 'remove');

  return { state, saveError, saving, save, savingLogo, uploadLogo, removeLogo };
}
